/**
 * RiderTableRow Component
 * 라이더 출금제한 테이블 행
 */
import React from 'react';
import { RiderListItem } from '../types/riderAccounting.types';
import { WITHDRAWAL_STATUS, WITHDRAWAL_STATUS_LABELS } from '@/shared/constants/statusCodes';

interface RiderTableRowProps {
    rider: RiderListItem;
    index: number;
    isTerminatedContract: boolean;
    getRiderRowClassName: (contStatus: string, balAmt: number | null | undefined) => string;
    getRiderContractStatusBadgeClass: (contStatus: string) => string;
    getRiderContractStatusBadge: (contStatus: string) => string;
    getPreAccountBadgeClass: (preAcctYn: string | null) => string;
    getPreAccountBadgeText: (preAcctYn: string | null) => string;
    formatNumber: (num: number | null | undefined) => string;
    onSendMoney: (riderName: string, amount: string, suppId: string, riderId: string) => void;
    onWithdrawalLimit: (riderName: string, riderId: string, suppId: string, withdrawalYn: string | null, targetAction: 'ALLOW' | 'RESTRICT') => void;
}

const RiderTableRow: React.FC<RiderTableRowProps> = ({
    rider,
    index,
    isTerminatedContract,
    getRiderRowClassName,
    getRiderContractStatusBadgeClass,
    getRiderContractStatusBadge,
    getPreAccountBadgeClass,
    getPreAccountBadgeText,
    formatNumber,
    onSendMoney,
    onWithdrawalLimit
}) => {
    // 계약종료면 무조건 비활성화
    const isButtonDisabled = isTerminatedContract;

    // 현재 출금 허용 상태인지 확인 (withdrawalYn='N'이면 출금허용)
    const isCurrentlyAllowed = rider.withdrawalYn === WITHDRAWAL_STATUS.ALLOWED; // 'N'
    // 현재 출금 제한 상태인지 확인 (withdrawalYn='Y'이면 출금제한)
    const isCurrentlyRestricted = rider.withdrawalYn === WITHDRAWAL_STATUS.RESTRICTED; // 'Y'
    // null 값인지 확인
    const isNullStatus = rider.withdrawalYn === null || rider.withdrawalYn === undefined;

    /**
     * 출금허용 버튼 클래스 반환
     * - 현재 출금허용 상태면: active (파란색, 클릭 불가)
     * - 현재 출금제한 상태 또는 null이면: inactive (회색, 클릭 가능)
     */
    const getAllowButtonClass = (): string => {
        const baseClass = 'btn btn-limit allow';
        const activeClass = isCurrentlyAllowed ? 'active' : 'inactive';
        return `${baseClass} ${activeClass}`.trim();
    };

    /**
     * 출금제한 버튼 클래스 반환
     * - 현재 출금제한 상태면: active (빨간색, 클릭 불가)
     * - 현재 출금허용 상태 또는 null이면: inactive (회색, 클릭 가능)
     */
    const getRestrictButtonClass = (): string => {
        const baseClass = 'btn btn-limit restrict';
        const activeClass = isCurrentlyRestricted ? 'active' : 'inactive';
        return `${baseClass} ${activeClass}`.trim();
    };

    return (
        <tr
            key={`${rider.riderId}-${index}`}
            className={getRiderRowClassName(rider.contStatus, rider.balAmt)}
        >
            {/* 협력사명 */}
            <td>{rider.suppName || '-'}</td>

            {/* 라이더명 */}
            <td>{rider.riderName || '-'}</td>

            {/* 계약상태 */}
            <td>
                <span className={`badge ${getRiderContractStatusBadgeClass(rider.contStatus)}`}>
                    {rider.contStatusName || getRiderContractStatusBadge(rider.contStatus)}
                </span>
            </td>

            {/* 라이더ID */}
            <td>{rider.riderId || '-'}</td>

            {/* 선정산 */}
            <td>
                <span className={`badge ${getPreAccountBadgeClass(rider.preAcctYn)}`}>
                    {getPreAccountBadgeText(rider.preAcctYn)}
                </span>
            </td>

            {/* 등록일자 */}
            <td>{rider.riderInsDate || '-'}</td>

            {/* 계약종료일자 */}
            <td>{rider.contEdDate || '-'}</td>

            {/* 잔액 */}
            <td className="number-cell">
                {rider.balAmt !== null && rider.balAmt !== undefined ? formatNumber(rider.balAmt) : ''}
            </td>

            {/* 미납금 */}
            <td className="number-cell">
                {rider.unpaidAmt !== null && rider.unpaidAmt !== undefined ? formatNumber(rider.unpaidAmt) : ''}
            </td>

            {/* 계좌관리 */}
            <td>
                <div className="action-buttons">
                    {/* 일차감 버튼 */}
                    <button
                        className={`btn btn-send ${isButtonDisabled ? 'disabled' : ''}`}
                        onClick={() => !isButtonDisabled && onSendMoney(rider.riderName, '-', rider.suppId, rider.riderId)}
                        disabled={isButtonDisabled}
                    >
                        일차감
                    </button>

                    {/* 출금허용 버튼 - 현재 출금허용(N) 상태면 파란색 표시, 클릭 불가 */}
                    <button
                        className={getAllowButtonClass()}
                        onClick={() => !isButtonDisabled && (isCurrentlyRestricted || isNullStatus) && onWithdrawalLimit(rider.riderName, rider.riderId, rider.suppId, rider.withdrawalYn, 'ALLOW')}
                        disabled={isButtonDisabled || isCurrentlyAllowed}
                    >
                        {WITHDRAWAL_STATUS_LABELS[WITHDRAWAL_STATUS.ALLOWED]}
                    </button>

                    {/* 출금제한 버튼 - 현재 출금제한(Y) 상태면 빨간색 표시, 클릭 불가 */}
                    <button
                        className={getRestrictButtonClass()}
                        onClick={() => !isButtonDisabled && (isCurrentlyAllowed || isNullStatus) && onWithdrawalLimit(rider.riderName, rider.riderId, rider.suppId, rider.withdrawalYn, 'RESTRICT')}
                        disabled={isButtonDisabled || isCurrentlyRestricted}
                    >
                        {WITHDRAWAL_STATUS_LABELS[WITHDRAWAL_STATUS.RESTRICTED]}
                    </button>
                </div>
            </td>
        </tr>
    );
};

export default RiderTableRow;
